import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import moment, { Moment } from 'moment';
import { Observable } from 'rxjs';
import DateHelper from 'src/app/helpers/date-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { ObservableHelper } from 'src/app/helpers/observable-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Employee from 'src/app/model/product/employee';
import { Adjustment } from 'src/app/model/project/adjustment';
import {
  SubProject,
  SUB_PROJECT_TYPES
} from 'src/app/model/project/sub-project';
import { SubProjectControllerRequestService } from 'src/app/services/api/sub-project-controller-request.service';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';

@Component({
  selector: 'planning-shortcut',
  templateUrl: './planning-shortcut.component.html',
  styleUrls: ['./planning-shortcut.component.scss']
})
export class PlanningShortcutComponent extends SubscriptionBasedComponent
  implements OnInit {
  @Input()
  subProject: SubProject;

  loading: boolean = true;

  printworkHandlerChoices: Employee[] = [];
  quotationHandlerChoices: Employee[] = [];
  designerChoices: Employee[] = [];
  finisherChoices: Employee[] = [];
  placerChoices: Employee[] = [];

  @Output()
  close = new EventEmitter();

  quotationEmployeeId: number;
  quotationDate: Moment;
  designEmployeeId: number;
  designDate: Moment;
  printworkEmployeeId: number;
  printworkDate: Moment;
  finishEmployeeId: number;
  finishDate: Moment;
  placementEmployeeId: number;
  placementDate: Moment;

  constructor(
    private snackService: SnackService,
    private employeeChoicesService: EmployeeChoicesService,
    private subProjectService: SubProjectControllerRequestService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.load();
  }

  private async load(): Promise<void> {
    await this.loadEmployees();
    this.assignPlanning(this.subProject);
    this.loading = false;
    this.cd.markForCheck();
  }

  private loadEmployees(): Promise<void> {
    this.employeeChoicesService.updateEmployeeChoices();
    const quotationEmployeesPromise = this.loadAndProcessEmployees(
      this.employeeChoicesService.getQuotationHandlerChoicesSubscribable(),
      (quotationHandlers: Employee[]) =>
        (this.quotationHandlerChoices = quotationHandlers)
    );
    const designEmployeesPromise = this.loadAndProcessEmployees(
      this.employeeChoicesService.getDesignerChoicesSubscribable(),
      (designers: Employee[]) => (this.designerChoices = designers)
    );
    const printworkHandlersPromise = this.loadAndProcessEmployees(
      this.employeeChoicesService.getPrintworkHandlerChoicesSubscribable(),
      (printworkHandlers: Employee[]) =>
        (this.printworkHandlerChoices = printworkHandlers)
    );
    const finishEmployeesPromise = this.loadAndProcessEmployees(
      this.employeeChoicesService.getFinisherChoicesSubscribable(),
      (finishers: Employee[]) => (this.finisherChoices = finishers)
    );
    const placerEmployeesPromise = this.loadAndProcessEmployees(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (placers: Employee[]) => (this.placerChoices = placers)
    );

    return Promise.all([
      quotationEmployeesPromise,
      designEmployeesPromise,
      printworkHandlersPromise,
      finishEmployeesPromise,
      placerEmployeesPromise
    ]).then(GeneralHelper.noop);
  }

  private async loadAndProcessEmployees(
    employeeChoicesObservable: Observable<Employee[]>,
    callback: (employeeChoices: Employee[]) => void
  ): Promise<void> {
    const employeeChoices = await ObservableHelper.untilFirstNonNull(
      employeeChoicesObservable
    ).toPromise();
    callback(employeeChoices);
  }

  assignPlanning(subProject: SubProject): void {
    const noEmployeeChoices = [];
    if (this.quotationHandlerChoices.length === 0) {
      noEmployeeChoices.push('offerte');
    }
    if (this.designerChoices.length === 0) {
      noEmployeeChoices.push('ontwerp');
    }
    if (this.printworkHandlerChoices.length === 0) {
      noEmployeeChoices.push('printwerk');
    }
    if (this.finisherChoices.length === 0) {
      noEmployeeChoices.push('afwerking');
    }
    if (this.placerChoices.length === 0) {
      noEmployeeChoices.push('plaatsing');
    }

    if (noEmployeeChoices.length > 0) {
      this.snackService.showWarning(
        'Er zijn geen medewerkers ingesteld voor ' +
          noEmployeeChoices.join(', ')
      );
      return;
    }

    this.quotationEmployeeId =
      subProject.quotationHandler != null
        ? subProject.quotationHandler.id
        : null;
    this.quotationDate =
      subProject.quotationByDate != null
        ? this.parseDate(subProject.quotationByDate)
        : null;

    if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.designEmployeeId =
        subProject.project.products[0].designer != null
          ? subProject.project.products[0].designer.id
          : null;
      this.printworkEmployeeId =
        subProject.project.products[0].printworkHandler != null
          ? subProject.project.products[0].printworkHandler.id
          : null;
      this.finishEmployeeId =
        subProject.project.products[0].finisher != null
          ? subProject.project.products[0].finisher.id
          : null;
      this.placementEmployeeId =
        subProject.project.products[0].productPlacement.placers[0] != null
          ? subProject.project.products[0].productPlacement.placers[0].id
          : null;

      this.designDate =
        subProject.project.products[0].designByDate != null
          ? this.parseDate(subProject.project.products[0].designByDate)
          : null;
      this.printworkDate =
        subProject.project.products[0].printworkByDate != null
          ? this.parseDate(subProject.project.products[0].printworkByDate)
          : null;
      this.finishDate =
        subProject.project.products[0].finishByDate != null
          ? this.parseDate(subProject.project.products[0].finishByDate)
          : null;
      this.placementDate =
        subProject.project.products[0].productPlacement.actualPlacementDate !=
        null
          ? this.parseDate(
              subProject.project.products[0].productPlacement
                .actualPlacementDate
            )
          : null;
    } else {
      const adjustment = subProject as Adjustment;
      this.designEmployeeId =
        adjustment.adjustmentDesigner != null
          ? adjustment.adjustmentDesigner.id
          : null;
      this.printworkEmployeeId =
        adjustment.adjustmentPrintworkHandler != null
          ? adjustment.adjustmentPrintworkHandler.id
          : null;
      this.finishEmployeeId =
        adjustment.adjustmentFinisher != null
          ? adjustment.adjustmentFinisher.id
          : null;
      this.placementEmployeeId =
        subProject.projectPlacement.placers[0] != null
          ? subProject.projectPlacement.placers[0].id
          : null;

      this.designDate =
        subProject.currentDesignDate != null
          ? this.parseDate(subProject.currentDesignDate)
          : null;
      this.printworkDate =
        subProject.currentPrintDate != null
          ? this.parseDate(subProject.currentPrintDate)
          : null;
      this.finishDate =
        subProject.currentFinishDate != null
          ? this.parseDate(subProject.currentFinishDate)
          : null;
      this.placementDate =
        subProject.currentConfirmedPlacementDate != null
          ? this.parseDate(subProject.currentConfirmedPlacementDate)
          : null;
    }
  }

  submitAssign(): void {
    const assignDto = {
      quotationEmployeeId: this.quotationEmployeeId,
      quotationDate: this.quotationDate,
      designEmployeeId: this.designEmployeeId,
      designDate: this.designDate,
      printworkEmployeeId: this.printworkEmployeeId,
      printworkDate: this.printworkDate,
      finishEmployeeId: this.finishEmployeeId,
      finishDate: this.finishDate,
      placementEmployeeId: this.placementEmployeeId,
      placementDate: this.placementDate
    };
    this.subProjectService
      .assignPlanning(this.subProject.id, assignDto)
      .then(() => {
        this.close.emit(true);
      });
  }

  private parseDate(value?: string | Moment | Date): Moment | null {
    if (StringHelper.isEmptyOrWhiteSpace(value as string)) {
      return null;
    } else if (typeof value === 'string' || value instanceof String) {
      return DateHelper.parseMoment(value);
    } else if (moment.isMoment(value)) {
      return value;
    } else {
      return DateHelper.parseMoment(value);
    }
  }
}
